import type { ReactNode } from 'react';
import { cn } from '@/shared/utils/cn';
import { getFloatingLabelClass } from './floatingLabel.utils';
import { FORM_FIELD_BRAND_ORANGE } from './formField.styles';
import type { FormFieldVariant } from './formField.types';

export interface FormFieldShellProps {
  variant: FormFieldVariant;
  label: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

function StackedLabel({ label, required }: { label: string; required: boolean }) {
  return (
    <label className="mb-1 text-sm text-gray-600">
      {label}
      {required && <span className="text-[#F15D47]"> *</span>}
    </label>
  );
}

function FieldError({ error, variant }: { error?: string; variant: FormFieldVariant }) {
  if (!error) {
    if (variant === 'stacked') {
      return <div className="mt-1 h-5" />;
    }
    return null;
  }

  if (variant === 'floating') {
    return (
      <p className="m-[7px] text-[13px] leading-normal" style={{ color: FORM_FIELD_BRAND_ORANGE }} role="alert">
        {error}
      </p>
    );
  }

  return (
    <div className="mt-1 h-5">
      <p className="text-xs text-red-600" role="alert">
        {error}
      </p>
    </div>
  );
}

export function FormFieldShell({
  variant,
  label,
  error,
  required = true,
  children,
  footer,
  className,
}: FormFieldShellProps) {
  const hasError = Boolean(error);

  if (variant === 'floating') {
    return (
      <div className={className}>
        {children}
        <FieldError error={error} variant={variant} />
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col', className)}>
      <StackedLabel label={label} required={required} />
      {children}
      {footer}
      <FieldError error={hasError ? error : undefined} variant={variant} />
    </div>
  );
}

export function FloatingLabel({ label, isActive }: { label: string; isActive: boolean }) {
  return (
    <span className={getFloatingLabelClass(isActive)}>
      {label}
    </span>
  );
}
